import React, { useEffect, useContext } from 'react';
import ReactJson from 'react-json-view';
import flowRight from 'lodash-es/flowRight';
import PropTypes from 'prop-types';
import { omit, mapValues, isPlainObject, isArray, map } from 'lodash-es';
import { useParams } from 'react-router-dom';
import { useAsObservableSource } from 'mobx-react';
import { autorun } from 'mobx';

import { withStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { AuthContext } from 'core/components/auth';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import OfferHeader from '../header';

const quoteViolationDates = ['homeClaimDates', 'homeViolationDates', 'homeSingleViolationDates', 'homeViolationPeople'];

const quoteFieldsForUnderwriters = ['homeViolations', 'homeClaimsForUW', 'offerings', ...quoteViolationDates];

/*
 * Cleans off the ssn and __typename keys off the object
 * Move to a helpers function in the future if it's needed elsewhere
 * and refactor to take in a array of keys to omit
 */
const cleanObj = (obj) => {
  return mapValues(omit(obj, ['ssn', '__typename']), (val, key) => {
    if (isPlainObject(val)) {
      return cleanObj(omit(val, ['ssn', '__typename']));
    }
    if (isArray(val)) {
      return map(val, cleanObj);
    }
    /*
    The keys of violationDates are dynamic since they are the dates of the violation.
    So, the schema represents it as AWSJSON which stringifies it, so we parse here before showing in details tree
    */
    if (['autoViolationDates', ...quoteViolationDates].includes(key)) {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return val;
        }
      }
    }
    return val;
  });
};

/*
 * Recursively parse AWSJSON string fields within the vendorData subtree.
 * Scoped separately from cleanObj to avoid colliding with generic field names
 * (e.g. 'name', 'age', 'status') that exist throughout the wider offer object.
 * https://github.com/gobranch/branch/issues/29244
 */
const parseVendorDataAwsJson = (obj) => {
  if (isArray(obj)) {
    return map(obj, parseVendorDataAwsJson);
  }
  if (isPlainObject(obj)) {
    return mapValues(obj, (val) => {
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return val;
        }
      }
      return parseVendorDataAwsJson(val);
    });
  }
  return obj;
};

const OfferDetails = ({
  store: {
    offer: { offer, loading, getOffer, vendorData, getOfferVendorData }
  }
}) => {
  const urlParams = useParams();
  const { isTeamLeader } = useContext(AuthContext);

  // Clean the offerId from URL params to handle trailing punctuation
  const cleanedOfferId = extractCleanOfferId(urlParams.offerId);

  if (!cleanedOfferId) {
    throw new Error('Invalid URL');
  }

  useAsObservableSource({ ...urlParams, offerId: cleanedOfferId });

  useEffect(() => {
    // autorun automatically tracks loading and offer reactively
    const dispose = autorun(() => {
      if (!loading && !offer) {
        getOffer(cleanedOfferId);
      }
    });
    return dispose; // Cleanup the autorun when component unmounts
  }, [cleanedOfferId, getOffer]);

  // Fetch vendor data separately — only for TeamLeaders (field is auth-gated in schema)
  useEffect(() => {
    if (isTeamLeader && cleanedOfferId) {
      getOfferVendorData(cleanedOfferId);
    }
  }, [isTeamLeader, cleanedOfferId, getOfferVendorData]);

  // Underwriter Managers can see the whole offer
  // Underwriters can see only information related to auto violations & drivers
  // https://github.com/gobranch/branch/issues/5180
  const cleanedOfferBase = cleanObj(offer);
  // Merge separately-fetched vendor data back into the offer for display
  const offerWithVendorData = vendorData
    ? { ...cleanedOfferBase, vendorData: parseVendorDataAwsJson(vendorData) }
    : cleanedOfferBase;
  // UW view is a strict allowlist of driver/quote fields — no vendor data needed as source
  const cleanedOfferForUW = {
    quote: {
      drivers: cleanedOfferBase.quote?.drivers.map((driver) => {
        const { firstName, middleName, lastName, age, suffix, autoViolationDates, autoViolationsForUW } = driver;
        return {
          firstName,
          middleName,
          lastName,
          age,
          suffix,
          autoViolationDates,
          autoViolationsForUW
        };
      }),
      ...quoteFieldsForUnderwriters.reduce(
        (acc, field) => (!cleanedOfferBase.quote ? acc : { ...acc, [field]: cleanedOfferBase.quote[field] }),
        {}
      )
    }
  };

  const autoOpenSections = [
    'offer details',
    'quote',
    'drivers',
    'autoViolationDates',
    'autoViolationsForUW',
    'autoViolations'
    // vendorData intentionally excluded - data volume too large to auto-expand
  ];
  const shouldCollapse = (field) => {
    return !autoOpenSections.includes(field.name);
  };

  if (loading || !offer) {
    return <Loading noBackground />;
  }

  return (
    <>
      <OfferHeader
        offerId={offer.id}
        options={{ showAddress: true, hideBackButton: true, showCustomerMatches: false }}
      />
      <ReactJson
        src={isTeamLeader ? offerWithVendorData : cleanedOfferForUW}
        displayDataTypes={false}
        name="offer details"
        collapsed={false}
        theme="bright:inverted"
        shouldCollapse={shouldCollapse}
      />
    </>
  );
};

OfferDetails.propTypes = {
  store: PropTypes.object.isRequired
};

export default flowRight(withStore)(OfferDetails);
